<template>
  <div class="ant-map-picker">
    <div v-if="showCoords" class="ant-map-picker__coords">
      <span>Lat: {{ innerLat.toFixed(6) }}</span>
      <span>Lng: {{ innerLng.toFixed(6) }}</span>
    </div>

    <GoogleMap
      v-if="apiKey"
      :api-key="apiKey"
      class="ant-map-picker__map"
      :style="{ width: '100%', height }"
      :center="center"
      :zoom="zoom"
      @click="onClickMap"
    >
      <Marker
        :options="markerOptions"
        :clickable="true"
        :draggable="draggable"
        @dragend="onDragEnd"
      />
    </GoogleMap>

    <div v-else class="ant-map-picker__empty">
      Thiếu Google Maps API key
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, reactive, ref, watch } from "vue";
import { GoogleMap, Marker } from "vue3-google-map";
import { GOOGLE_MAP_API } from "@/config/app";
import type { MapLocation } from "./types";

export type { MapLocation };

interface Props {
  modelValue?: MapLocation | null;
  lat?: number;
  lng?: number;
  zoom?: number;
  height?: string;
  draggable?: boolean;
  showCoords?: boolean;
  apiKey?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  lat: 21.028511,
  lng: 105.804817,
  zoom: 15,
  height: "360px",
  draggable: true,
  showCoords: true,
  apiKey: GOOGLE_MAP_API,
});

const emit = defineEmits<{
  (e: "update:modelValue", value: MapLocation): void;
  (e: "update:lat", value: number): void;
  (e: "update:lng", value: number): void;
  (e: "change", value: MapLocation): void;
  (e: "pick", value: MapLocation): void;
}>();

const zoom = ref(props.zoom);
const center = reactive({
  lat: props.modelValue?.lat ?? props.lat,
  lng: props.modelValue?.lng ?? props.lng,
});
const marker = reactive({
  lat: props.modelValue?.lat ?? props.lat,
  lng: props.modelValue?.lng ?? props.lng,
});

const innerLat = computed(() => marker.lat);
const innerLng = computed(() => marker.lng);

const markerOptions = computed(() => ({
  position: {
    lat: marker.lat,
    lng: marker.lng,
  },
}));

const syncFromProps = () => {
  const nextLat = props.modelValue?.lat ?? props.lat;
  const nextLng = props.modelValue?.lng ?? props.lng;
  center.lat = nextLat;
  center.lng = nextLng;
  marker.lat = nextLat;
  marker.lng = nextLng;
  if (props.modelValue?.zoom != null) {
    zoom.value = props.modelValue.zoom;
  }
};

watch(
  () => [props.modelValue, props.lat, props.lng, props.zoom] as const,
  () => syncFromProps(),
  { deep: true }
);

const emitLocation = (lat: number, lng: number) => {
  const payload: MapLocation = { lat, lng, zoom: zoom.value };
  emit("update:modelValue", payload);
  emit("update:lat", lat);
  emit("update:lng", lng);
  emit("change", payload);
  emit("pick", payload);
};

const onClickMap = (event: any) => {
  const lat = event?.latLng?.lat?.() ?? event?.latLng?.lat;
  const lng = event?.latLng?.lng?.() ?? event?.latLng?.lng;
  if (typeof lat !== "number" || typeof lng !== "number") return;

  center.lat = lat;
  center.lng = lng;
  marker.lat = lat;
  marker.lng = lng;
  emitLocation(lat, lng);
};

const onDragEnd = (event: any) => {
  const lat = event?.latLng?.lat?.() ?? event?.latLng?.lat;
  const lng = event?.latLng?.lng?.() ?? event?.latLng?.lng;
  if (typeof lat !== "number" || typeof lng !== "number") return;

  marker.lat = lat;
  marker.lng = lng;
  center.lat = lat;
  center.lng = lng;
  emitLocation(lat, lng);
};
</script>

<style scoped>
.ant-map-picker {
  width: 100%;
}

.ant-map-picker__coords {
  display: flex;
  gap: 16px;
  margin-bottom: 8px;
  font-size: 13px;
  color: #666;
}

.ant-map-picker__map {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #eee;
}

.ant-map-picker__empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  border: 1px dashed #d9d9d9;
  border-radius: 8px;
  color: #999;
}
</style>

<!--
  Hướng dẫn sử dụng — Map Picker
  Global: <h-mapPicker v-model="location" height="400px" @pick="onPick" />
  Import: import { MapPicker } from '@/components/ant/address'

  v-model: { lat, lng, zoom? }
  Props: lat, lng, zoom, height, draggable, showCoords, apiKey
  Events: update:modelValue, update:lat, update:lng, change, pick
  Dùng GOOGLE_MAP_API từ @/config/app
  Docs: src/components/ant/address/README.md
-->
